import { accountKo } from "./account-ko.js";
import { connectKo } from "./connect-ko.js";
import type { Messages } from "./en.js";
import { landingKo } from "./landing-ko.js";

// Korean pack. Language packs are the one place where committed text is not English.
// Keep technical terms that readers search for in their original form: MCP, SKILL.md, browse_repo, search_repo, load_skill.

export const ko: Messages = {
  meta: {
    siteName: "SkillCDN",
    landing: {
      title: "SkillCDN: AI 에이전트로 운영하는 1인 회사",
      description:
        "전문가의 방식을 스킬로 받으면 내 AI가 마케터, 디자이너, 개발자가 됩니다. 만들고 싶은 것을 검색하고, 일은 보드에 올리고, 에이전트에 맡기세요. 빌더에 합류하세요.",
      about:
        "SkillCDN은 스킬과 문서를 담은 git 저장소, 곧 팩을 MCP 서버로 ChatGPT나 Claude 같은 AI 앱에 제공해, 앱이 그 안의 스킬을 불러와 대화 속에서 따르게 합니다. 한 배포는 제공하는 모든 팩을 한 번에 검색하고, 콘솔 팩은 회사의 일을 에이전트와 함께 보드에서 돌립니다.",
    },
    explore: {
      title: "만들고 싶은 것 찾기 | SkillCDN",
      description:
        "모든 공개 팩의 스킬과 문서를 검색하거나, 검증된 팩에서 고르거나, 저장소 주소를 열어 보세요. 팩마다 AI 앱을 연결하는 안내가 함께 있습니다.",
    },
    search: {
      title: "스킬과 문서 검색 | SkillCDN",
      description:
        "모든 공개 팩에서 스킬과 문서를 단어와 의미로 찾고, 결과가 속한 팩을 내 AI 앱에 바로 연결하세요.",
      resultsTitle: (term: string) => `${term} | SkillCDN`,
      resultsDescription: (term: string, count: number, packs: number) =>
        count === 0
          ? `SkillCDN의 공개 팩 ${packs}개에서 “${term}”에 맞는 스킬이나 문서를 찾지 못했습니다.`
          : `SkillCDN의 공개 팩 ${packs}개에서 찾은 “${term}” 관련 스킬과 문서 ${count}건. 각 결과가 속한 팩을 AI 앱에 바로 연결할 수 있습니다.`,
    },
    mount: {
      title: (repository: string) => `${repository} | SkillCDN`,
      description: (repository: string) =>
        `${repository} 저장소가 SkillCDN을 통해 에이전트에 제공하는 스킬과 문서입니다.`,
      summary: (repository: string, skills: number, documents: number, names: readonly string[]) =>
        names.length === 0
          ? `${repository} 저장소가 SkillCDN을 통해 MCP로 AI 에이전트에 제공하는 스킬 ${skills}개와 문서 ${documents}개입니다.`
          : `${repository} 저장소가 SkillCDN을 통해 MCP로 AI 에이전트에 제공하는 스킬 ${skills}개와 문서 ${documents}개: ${names.join(", ")}.`,
      skillTitle: (skill: string, repository: string) => `${skill} · ${repository} | SkillCDN`,
      skillDescription: (skill: string, repository: string, description: string) =>
        `${repository} 저장소의 AI 에이전트용 스킬 ${skill}: ${description}`,
      fileTitle: (path: string, repository: string) => `${path} · ${repository} | SkillCDN`,
    },
    notFound: {
      title: "페이지를 찾을 수 없습니다 | SkillCDN",
      description: "이 주소에는 아무것도 없습니다.",
    },
    ogImageAlt: "SkillCDN: 혼자 시작하세요, 팀은 이미 있습니다",
  },

  nav: {
    home: "SkillCDN 홈",
    explore: "둘러보기",
    search: "검색",
    docs: "문서",
    github: "GitHub",
    skipToContent: "본문으로 건너뛰기",
    main: "주 메뉴",
  },

  language: {
    label: "언어",
  },

  common: {
    copy: "복사",
    copied: "복사됨",
    loading: "불러오는 중…",
    retry: "다시 시도",
    reload: "처음부터 다시 불러오기",
    back: "뒤로",
  },

  address: {
    label: "저장소 주소",
    prefixHint: "GitHub",
    placeholder: "owner/repo",
    submit: "열기",
    hint: "필요하면 뒤에 @브랜치(또는 태그, 커밋)와 /폴더 경로를 붙일 수 있습니다.",
    examples: "예시",
    invalid: "올바른 주소가 아닙니다.",
    errors: {
      missing_repo: "owner/repo 형식으로 입력하세요.",
      invalid_owner: "GitHub에서 쓸 수 없는 소유자 이름입니다.",
      invalid_repo: "GitHub에서 쓸 수 없는 저장소 이름입니다. 끝의 .git은 빼 주세요.",
      empty_ref: "@ 뒤에 브랜치, 태그 또는 커밋이 빠졌습니다.",
      invalid_ref: "올바른 브랜치, 태그 또는 커밋 이름이 아닙니다.",
      invalid_path: "경로에는 . 또는 .. 세그먼트, 역슬래시, 빈 세그먼트를 쓸 수 없습니다.",
      dot_segment: "경로에는 . 또는 .. 세그먼트를 쓸 수 없습니다.",
      empty_segment: "주소에 빈 세그먼트가 있습니다. 중복되거나 끝에 붙은 슬래시를 지워 주세요.",
      unknown_host: "지금은 GitHub 저장소(gh)만 지원합니다.",
      too_long: "주소가 너무 깁니다.",
      bad_encoding: "주소에 잘못되었거나 허용되지 않는 퍼센트 이스케이프가 있습니다.",
      forbidden_character: "주소에 제어 문자 또는 보이지 않는 문자가 있습니다.",
      not_absolute: "owner/repo 형식으로 입력하세요.",
    },
  },

  connect: connectKo,

  ...accountKo,

  landing: landingKo,

  explore: {
    kicker: "둘러보기",
    title: "만들고 싶은 것을 찾아보세요.",
    lead: "검색하거나, 검증된 팩에서 고르거나, 주소를 붙이거나.",
    featuredKicker: "추천 팩",
    featured: "검증된 팩, 바로 연결됩니다.",
    featuredLead: "팩을 열면 쓰는 AI별 연결 안내가 있습니다.",
    featuredSkills: (count: number) => `스킬 ${count}개`,
    featuredDocuments: (count: number) => `문서 ${count}개`,
    moreSkills: (count: number) => `외 ${count}개`,
    featuredIndexing: "색인 중…",
    featuredFailed: "색인하지 못했습니다",
    pages: "추천 스킬 페이지",
    page: (current: number, total: number) => `${total}쪽 중 ${current}쪽`,
    previous: "이전",
    next: "다음",
  },

  packKinds: {
    skills: "스킬 팩",
    documents: "문서 팩",
    repository: "저장소",
    legend: {
      title: "종류 읽는 법",
      skills: "스킬을 담은 저장소",
      documents: "문서만 담은 저장소",
      repository: "매니페스트 없이 읽힌 저장소",
    },
  },

  search: {
    label: "스킬과 문서 검색",
    placeholder: "무엇을 만들까요? 카드뉴스, 쇼츠 광고, 결제 되는 웹사이트…",
    submit: "검색",
    heading: (term: string) => `“${term}” 검색 결과`,
    bare: {
      title: "무엇을 만들까요?",
      lead: "모든 공개 팩의 스킬과 문서를 단어와 의미로 찾습니다.",
    },
    found: (count: number, packs: number) => `공개 팩 ${packs}개에서 ${count}건`,
    ranking: { words: "단어로 순위를 매김", meaning: "단어와 의미로 순위를 매김" },
    none: "일치하는 것을 찾지 못했습니다. 다른 말로, 또는 팩이 쓰인 언어의 말로 찾아보세요.",
    kinds: { skill: "스킬", document: "문서" },
    verified: "검증됨",
    filters: { all: "전체", skill: "스킬", document: "문서" },
    actions: {
      skill: "스킬 열기",
      document: "문서 열기",
      section: "이 섹션에서 열기",
      pack: "팩 열기",
      repository: "저장소 열기",
      more: "이 팩 안에서 더 찾기",
      moreRepository: "이 저장소 안에서 더 찾기",
    },
    partOf: (directory: string) => `${directory} 스킬에 속한 파일`,
    aside: {
      address: { title: "주소를 아세요?", body: "저장소 주소를 열면 바로 그 팩으로 갑니다." },
    },
  },

  docs: {
    title: "문서",
    lead: "쓰던 AI에서 팩을 쓰는 법, 직접 만드는 법, 그리고 그 둘을 뒷받침하는 규약.",
    metaTitle: "문서 | SkillCDN",
    metaDescription:
      "팩을 쓰고 만드는 가이드와 SkillCDN이 구현하는 명세: 포맷, 주소, 에이전트가 MCP로 받는 것, REST API, 그리고 직접 운영하는 법.",
    pageTitle: (title: string) => `${title} · 문서 | SkillCDN`,
    sections: { guides: "가이드", reference: "레퍼런스", operate: "운영" },
    navLabel: "문서 목차",
    menu: "메뉴",
    onThisPage: "이 페이지에서",
    inEnglish: "이 페이지는 영어로 쓰여 있습니다.",
    edit: "GitHub에서 이 페이지 수정하기",
    source: "Markdown 원문",
    allInOne: "전체 문서를 한 파일로",
    neighbours: "이전 페이지와 다음 페이지",
    previous: "이전",
    next: "다음",
  },

  mount: {
    repository: "저장소",
    defaultBranch: "기본 브랜치",
    ref: "ref",
    pinned: "고정된 커밋",
    commit: "커밋",
    path: "경로",
    license: "라이선스",
    verified: "인증됨",
    verifiedHint: "저장소 소유자 또는 이 사이트 운영자가 보증한 저장소입니다.",
    unverified: "미확인",
    unverifiedHint:
      "이 콘텐츠는 저장소에서 그대로 가져온 것이며, 저장소 소유자가 SkillCDN에서 확인 절차를 거치지 않았습니다. 신뢰하기 전에 직접 검토하세요.",
    viewOnHost: "GitHub에서 보기",
    private: "비공개",
    privateHint: "GitHub에서 이 저장소를 볼 수 있는 사람만 이 페이지를 열고 연결할 수 있어요.",
    use: {
      these: "내 AI에 이 주소를 연결하면 이 스킬들을 쓸 수 있어요.",
      this: "내 AI에 이 주소를 연결하면 이 스킬을 쓸 수 있어요.",
      guide: "연결 방법 보기",
    },
    lost: {
      lead: "주소를 확인하거나, 추천 스킬에서 시작해 보세요.",
      explore: "추천 스킬 보기",
    },
    indexing: {
      title: "이 커밋을 색인하는 중…",
      body: "보통 몇 초면 끝납니다. 페이지는 자동으로 갱신됩니다.",
      slow: "아직 색인 중입니다. 큰 저장소는 더 오래 걸립니다. 나중에 이 페이지로 돌아오셔도 됩니다.",
    },
    failed: {
      title: "이 커밋을 색인하지 못했습니다",
      body: (code: string) => `사유: ${code}. 자동으로 다시 시도하니 잠시 후 다시 확인해 주세요.`,
    },
    newest: {
      title: (commit: string) => `커밋 ${commit} 기준으로 보여 주고 있습니다`,
      indexing: (commit: string) =>
        `최신 커밋(${commit})을 색인하는 중입니다. 준비될 때까지 이 페이지와 모든 에이전트에는 마지막으로 색인된 커밋이 제공됩니다.`,
      failed: (commit: string, code: string) =>
        `최신 커밋(${commit})을 색인하지 못했습니다 (${code}). 자동으로 다시 시도하며, 그때까지 이 페이지와 모든 에이전트에는 마지막으로 색인된 커밋이 제공됩니다.`,
    },
    rebuilding: {
      title: "이 커밋의 색인을 다시 만드는 중",
      body: "읽기 규칙이 바뀌었습니다. 새 색인이 준비될 때까지 이 페이지와 모든 에이전트에는 이전 규칙으로 만든 색인이 제공됩니다.",
      failed: (code: string) =>
        `색인을 다시 만들지 못했습니다 (${code}). 자동으로 다시 시도하며, 그때까지 이 페이지와 모든 에이전트에는 이전 규칙으로 만든 색인이 제공됩니다.`,
    },
    truncated: "저장소가 색인 한도보다 커서 일부 파일이 색인에서 빠졌습니다.",
    indexNow: {
      button: "지금 색인",
      hint: "최신 커밋을 지금 읽고, 그 문서들을 가장 먼저 임베딩합니다. GitHub에서 이 저장소를 관리하는 사람에게 보입니다.",
      queued: "대기열에 넣었습니다. 색인과 임베딩이 진행되는 대로 페이지가 갱신됩니다.",
      already: "이미 대기 중입니다. 다음 차례예요.",
    },
    tabs: {
      skills: (count: number) => `스킬 (${count})`,
      documents: (count: number) => `문서 (${count})`,
      diagnostics: (count: number) => `제외됨 (${count})`,
    },
    listLimited: (shown: number, total: number) =>
      `전체 ${total}개 중 처음 ${shown}개만 표시합니다.`,
    noSkills: {
      title: "스킬이 없습니다",
      body: "올바른 SKILL.md가 있는 폴더가 없습니다. 문서는 그대로 검색하고 읽을 수 있습니다.",
    },
    noDocuments: "문서가 없습니다.",
    empty: {
      title: "제공할 내용이 없습니다",
      body: "이 주소에는 스킬도, Markdown이나 JSON 문서도 없습니다.",
    },
    diagnostics: {
      lead: "아래 매니페스트는 제공되지 않았습니다. 수정해서 푸시하면 다음 커밋부터 다시 색인됩니다.",
    },
    warnings: (count: number) => `경고 ${count}개`,
    browse: {
      title: "저장소 둘러보기",
      root: "전체",
      breadcrumb: "저장소 폴더",
      introduction: "소개 읽기",
      folders: "폴더",
      skills: "스킬",
      documents: "문서",
      empty: "이 폴더에 스킬이나 파일이 없습니다.",
      counts: (skills: number, documents: number) => `스킬 ${skills}개 · 문서 ${documents}개`,
      holds: (skills: number, documents: number) => {
        const parts = [
          ...(skills === 0 ? [] : [`스킬 ${skills}개`]),
          ...(documents === 0 ? [] : [`문서 ${documents}개`]),
        ];
        return parts.length === 0 ? "아직 스킬이 없어요" : parts.join(" · ");
      },
      nested: (count: number) => `안에 스킬 ${count}개`,
      describedOnly: "설명만 제공",
      describedOnlyHint:
        "라이선스가 SkillCDN에 허용하는 것은 이 스킬이 있다고 알리는 것까지이며, 내용을 전달하는 것은 아닙니다.",
    },
    search: {
      label: "이 폴더와 하위 폴더 검색",
      placeholder: "이 폴더에서 검색…",
      hint: "스킬과 문서의 원문에서 찾습니다. 화면용 번역은 검색되지 않아요.",
      hintMeaning: "스킬과 문서의 원문에서 단어와 의미로 찾습니다. 화면용 번역은 검색되지 않아요.",
      mode: "검색",
      words: "단어",
      meaning: "단어와 의미",
      wordsHint: "에이전트가 스킬과 문서를 그 안에 쓰인 단어로 찾습니다.",
      meaningHint:
        "에이전트가 스킬과 문서를 단어와 의미로 찾습니다. 다른 말로 물어도 해당 구절을 찾아요.",
      embedding: (embedded: number, total: number) => `문서 ${total}개 중 ${embedded}개 임베딩됨`,
      submit: "검색",
      clear: "지우기",
      resultsFor: (query: string) => `“${query}” 검색 결과`,
      none: "이 폴더에서 일치하는 항목을 찾지 못했습니다. 다른 검색어나 상위 폴더에서 찾아보세요.",
      sections: "일치한 섹션",
      sectionStart: "문서 시작 부분",
    },
    kinds: { skill: "스킬", document: "문서" },
    partOfSkill: (directory: string) => `${directory} 스킬에 속한 파일`,
  },

  skill: {
    all: "전체 스킬과 문서",
    translationNote: "화면에 표시하기 위한 번역입니다. 에이전트는 스킬 원문을 읽습니다.",
    name: "이름",
    directory: "디렉터리",
    license: "라이선스",
    licenseNone: "선언되지 않음",
    licenseUnrecognized: "알 수 없는 라이선스",
    licenseFile: "GitHub에서 라이선스 파일 읽기",
    describedOnly:
      "이 스킬은 여기서 설명만 제공되고 내용은 제공되지 않습니다. 라이선스가 SkillCDN에 허용하는 것은 스킬이 있다고 알리는 것까지이며, 내용을 전달하는 것은 아닙니다.",
    readAtSource: "원본에서 읽기",
    compatibility: "호환성",
    allowedTools: "허용된 도구",
    metadata: "메타데이터",
    files: "이 스킬의 파일",
    filesTruncated: "앞쪽 파일만 표시됩니다.",
    oversized:
      "이 서버의 파일 크기 한도를 넘어 스킬과 함께 제공되지 않는 파일입니다. 제공되는 SKILL.md에 각 파일이 원본의 바이트로 가는 이 링크와 함께 적혀 있습니다.",
    browseFiles: "지원 파일 전체 둘러보기",
    browseChildren: "파일과 하위 스킬 둘러보기",
    incomplete:
      "스킬의 지침이 아직 전부 로드되지 않았습니다. 계속 읽어 상위 폴더의 규칙과 필수 내용을 확인하세요.",
    moreContext: "남은 지침 불러오기",
    noFiles: "이 스킬에는 보조 파일이 없습니다.",
    included: "스킬과 함께 전달됨",
    warnings: "작성자를 위한 경고",
    root: "(저장소 루트)",
    rules: "상위 폴더에서 적용된 규칙",
    rulesSource: (path: string) => `${path}에서 가져왔습니다. 이 스킬에 적용되는 규칙입니다.`,
    rulesAbove:
      "마운트된 디렉터리 위에 있는 저장소 매니페스트에서 가져왔습니다. 에이전트는 스킬을 불러올 때마다 이 규칙을 함께 받습니다.",
    rulesTruncated: "앞부분만 표시됩니다. 전체 내용은 매니페스트에 있습니다.",
  },

  file: {
    rendered: "렌더링",
    source: "원문",
    showing: (from: number, to: number, total: number) =>
      `전체 ${total.toLocaleString("ko")}자 중 ${from.toLocaleString("ko")}~${to.toLocaleString("ko")}자`,
    more: "더 불러오기",
    referenceUnavailable: "이 연결에서 읽을 수 없는 참조입니다.",
    directory: "디렉터리",
    bytes: (count: number) => `${count.toLocaleString("ko")}바이트`,
    directoryTruncated: "앞쪽 항목만 표시됩니다.",
    openAtSource: "파일이 있는 곳에서 열기",
  },

  errors: {
    title: "문제가 발생했습니다",
    generic: "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.",
    network: "서버에 연결하지 못했습니다. 네트워크 상태를 확인하고 다시 시도해 주세요.",
    codes: {
      "index.indexing": {
        title: "이 커밋을 색인하는 중…",
        body: "색인이 완료되면 파일을 읽을 수 있습니다. 잠시 후 다시 시도해 주세요.",
      },
      "index.failed": {
        title: "이 커밋을 색인하지 못했습니다",
        body: "아직 파일을 읽을 수 없습니다. 색인은 자동으로 다시 시도하니 잠시 후 다시 확인해 주세요.",
      },
      "mount.repo_not_found": {
        title: "저장소를 찾을 수 없습니다",
        body: "존재하지 않거나 비공개 저장소입니다.",
      },
      "index.not_administrator": {
        title: "요청할 권한이 없습니다",
        body: "GitHub에서 이 저장소를 관리하는 사람만 지금 색인을 요청할 수 있습니다.",
      },
      "index.unavailable": {
        title: "워커가 없습니다",
        body: "이 배포에는 요청을 받아 색인하는 워커가 없습니다.",
      },
      "mount.ref_not_found": {
        title: "ref를 찾을 수 없습니다",
        body: "이 저장소에 그런 브랜치나 태그가 없고, 기본 브랜치의 기록에 그런 커밋도 없습니다. ref에 슬래시가 들어 있다면 콜론으로 끝내세요. 예: @release/1.2:",
      },
      "token.limit": {
        title: "토큰을 더 만들 수 없습니다",
        body: "한 계정이 가질 수 있는 토큰 수에는 한도가 있습니다. 쓰지 않는 토큰을 삭제해 주세요.",
      },
      "token.public_repository": {
        title: "토큰이 필요 없습니다",
        body: "공개 저장소는 누구나 토큰 없이 읽을 수 있습니다.",
      },
      "token.invalid": {
        title: "이 토큰은 만들 수 없습니다",
        body: "저장소와 만료 기간을 고르고, 짧은 이름을 붙여 주세요.",
      },
      "token.not_found": {
        title: "토큰을 찾을 수 없습니다",
        body: "이미 삭제된 토큰입니다.",
      },
      "mount.not_allowed": {
        title: "여기서는 제공하지 않습니다",
        body: "이 배포에서는 이 저장소를 제공하지 않습니다.",
      },
      "mount.rate_limited": {
        title: "git 호스트가 요청을 제한하고 있습니다",
        body: "잠시 후 다시 시도해 주세요.",
      },
      "mount.unavailable": {
        title: "git 호스트에 연결하지 못했습니다",
        body: "잠시 후 다시 시도해 주세요.",
      },
      "skill.not_found": {
        title: "스킬을 찾을 수 없습니다",
        body: "이 주소에는 그런 이름의 스킬이 없습니다.",
      },
      "skill.ambiguous": {
        title: "같은 이름의 스킬이 여러 개입니다",
        body: "디렉터리를 골라서 열어 주세요.",
      },
      "skill.unavailable": {
        title: "지금은 스킬을 읽을 수 없습니다",
        body: "색인은 되어 있지만 내용을 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.",
      },
      "file.not_found": {
        title: "파일을 찾을 수 없습니다",
        body: "이 저장소의 해당 경로에는 파일이 없습니다.",
      },
      "file.too_large": {
        title: "파일이 너무 큽니다",
        body: "읽을 수 있는 크기 한도를 넘는 파일입니다.",
      },
      "file.not_text": {
        title: "텍스트 파일이 아닙니다",
        body: "여기서는 UTF-8 텍스트 파일만 읽을 수 있습니다.",
      },
      "search.busy": {
        title: "지금 검색이 몰려 있습니다",
        body: "검색 요청이 한꺼번에 많이 들어오고 있습니다. 잠시 후 다시 시도해 주세요.",
      },
      "search.timeout": {
        title: "검색이 제한 시간을 넘겼습니다",
        body: "더 짧은 말로, 또는 잠시 후 다시 찾아보세요.",
      },
    },
  },

  notFound: {
    title: "페이지를 찾을 수 없습니다",
    body: "이 주소에는 아무것도 없습니다.",
    home: "첫 페이지로 가기",
  },

  consent: {
    label: "쿠키 동의",
    title: "쿠키를 사용해요",
    text: "더 나은 서비스를 만들기 위한 이용 통계에 쓰여요. 선택은 언제든 바꿀 수 있어요.",
    privacy: "개인정보 처리방침",
    accept: "동의하기",
    decline: "괜찮아요",
    choices: "쿠키 설정",
    onTitle: "쿠키를 사용하고 있어요",
    onText: "더 나은 서비스를 만들기 위한 이용 통계에 쓰여요. 원하지 않으면 끌 수 있어요.",
    offTitle: "쿠키를 사용하지 않아요",
    offText: "이용 통계용 쿠키가 꺼져 있어요. 언제든 다시 켤 수 있어요.",
    keep: "확인",
    turnOff: "쿠키 끄기",
    turnOn: "쿠키 허용",
    close: "닫기",
  },

  legal: {
    revised: (date: string) => `최종 개정일 ${date}`,
  },

  footer: {
    tagline: "쓰던 AI에 더하는 스킬.",
    source: "GitHub의 소스",
    license: "라이선스",
    terms: "이용약관",
    privacy: "개인정보 처리방침",
    report: "콘텐츠 신고",
  },
};
